/**
 * fragments.ts — Helpers for the FragmentId addressing scheme.
 *
 * Scheme: `author:<author-id>/<section>/<subsection?>/<index?>`
 * Examples produced by fragmentId():
 *   author:john-keats/biography/p2
 *   author:john-keats/works/hyperion
 *   author:john-keats/works/hyperion/themes/p1
 *   author:john-keats/works/hyperion/passages/hyperion-beauty
 *   author:john-keats/theme_treatments/romanticism/p3
 *
 * The branded FragmentId type (declared in src/data/schema.ts) ensures
 * only strings produced by this helper end up in `data-fragment-id`
 * attributes — preventing free-form strings from leaking in.
 *
 * The future lesson-builder will read these attributes to build a stable
 * selection API. This file is the SOLE source of truth for the format —
 * if the scheme ever changes, update this file and re-render.
 */

import type { FragmentId } from '@/data/schema';

/**
 * Sections that can appear in a fragment-id.
 *
 * `works` is special: it's followed by a work-id slug, and then optionally
 * by a work-sub-section (summary | structure | themes | ...) + paragraph index
 * or a `passages/<passage-id>`.
 */
export type AuthorSection =
  | 'tagline'
  | 'biography'
  | 'historical_context'
  | 'style_and_themes'
  | 'significance'
  | 'works'
  | 'theme_treatments'
  | 'relations';

export type WorkSection =
  | 'one_line'
  | 'summary'
  | 'structure'
  | 'characters'
  | 'themes'
  | 'form_and_style'
  | 'reception_and_legacy'
  | 'passages'
  | 'sub_works';

/**
 * Build a FragmentId from raw parts. Parts after the author-id are
 * joined with '/' — pass the slugs/indexes verbatim.
 *
 * Example:
 *   fragmentId('john-keats', 'biography', 'p2')
 *     → 'author:john-keats/biography/p2'
 *   fragmentId('john-keats', 'works', 'hyperion', 'themes', 'p1')
 *     → 'author:john-keats/works/hyperion/themes/p1'
 */
export function fragmentId(authorId: string, ...path: string[]): FragmentId {
  return `author:${authorId}/${path.join('/')}` as FragmentId;
}

/** Paragraph index helper: paragraphIndex(0) → 'p1', paragraphIndex(1) → 'p2', ... */
export function paragraphIndex(zeroBased: number): string {
  return `p${zeroBased + 1}`;
}

/**
 * Parse a FragmentId into a structured object. Returns null if malformed.
 * Useful for the future lesson-builder reverse lookup.
 */
export interface ParsedFragmentId {
  authorId: string;
  path: string[];
  /** Convenience: first path segment (e.g. 'biography', 'works'). */
  section: AuthorSection | string;
}

export function parseFragmentId(id: string): ParsedFragmentId | null {
  if (!id.startsWith('author:')) return null;
  const body = id.slice('author:'.length);
  const parts = body.split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const [authorId, ...path] = parts;
  return { authorId, path, section: path[0] };
}
